import {
  Box,
  Paper,
  Table,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  Typography,
  TableSortLabel,
  TableContainer,
  TablePagination,
  CircularProgress,
  IconButton,
  Button,
  Tooltip,
} from '@mui/material';
import { IColumnDefinition } from '@common/types';
import {
  ColumnNames,
  IAssessmentsTableEntry,
  IAssessmentsTableFilters,
  useAssessmentsTable,
} from './hooks';
import dayjs from 'dayjs';
import { CreateAssessment } from '../CreateAssessment';
import { Edit, Download } from '@mui/icons-material';
import complete from '@assets/complete.svg';
import incomplete from '@assets/incomplete.svg';
import { theme } from '@store';
import { Select } from 'ui';

import { useDownloadAssessmentPdf } from '../../hooks/useDownloadAssessmentPdf';

import { t } from 'i18next';
import { TableNoResultPlaceholder } from '@components';
import { AssessmentDrawer } from 'views/pages/Patient/components/Assessments/components/AssessmentDrawer/AssessmentDrawer.tsx';

interface IAssessmentsTableProps {
  rows: IAssessmentsTableEntry[];
  columns: IColumnDefinition[];
  filters: IAssessmentsTableFilters;
  setFilters: (filters: IAssessmentsTableFilters) => void;
  handleResetFilter: () => void;
  setItemEditId: (id: string) => void;
  totalCount: number;
  loading: boolean;
  isFilterDisabled: boolean;
  patient_id: string;
  disabled: boolean;
  tabPanelInstrumentHeight: number;
  isReadOnly?: boolean;
}

export const AssessmentsTable = ({
  rows,
  filters,
  columns,
  setFilters,
  totalCount,
  loading,
  patient_id,
  setItemEditId,
  disabled = false,
  tabPanelInstrumentHeight,
  handleResetFilter,
  isFilterDisabled,
}: IAssessmentsTableProps) => {
  const { downloadPdf, isDownloading } = useDownloadAssessmentPdf();
  const { sort_by, sort_order } = filters;
  const {
    handleSelectAssessmentType,
    handleSelectCompletionStatus,
    handleSelectDate,
    handleChangeOrder,
    onPageIndexChange,
    onPageSizeChange,
    completedOptions,
    assessmentTypeOptions,
    assessmentDateOptions,
    isDrawerOpen,
    drawerDetails,
    handleRowClick,
    handleCloseDrawer,
    handleEditAssessment,
  } = useAssessmentsTable({ filters, setFilters, patient_id, setItemEditId });

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          maxHeight: '65vh',
        }}
      >
        <Box
          sx={{
            width: '100%',
            padding: '1rem',
            display: 'flex',
            justifyContent: 'space-between',
            bgcolor: 'white',
            borderBottom: theme => `1px solid ${theme.palette.grey[300]}`,
            alignItems: 'center',
          }}
        >
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '70%' }}
          >
            <Select
              sx={{ maxWidth: '350px', minWidth: '30%' }}
              name='assessment_type'
              label='Assessment'
              value={filters.type}
              onChange={handleSelectAssessmentType}
              fullWidth
              options={assessmentTypeOptions}
            />
            <Select
              sx={{ maxWidth: '350px', minWidth: '30%' }}
              name='completed'
              label='Status'
              value={filters.completed}
              onChange={handleSelectCompletionStatus}
              options={completedOptions}
            />
            <Select
              sx={{ maxWidth: '350px', minWidth: '30%' }}
              name='assessment_date'
              label='Assessment date'
              value={filters.date}
              onChange={handleSelectDate}
              options={assessmentDateOptions}
            />
            <Button onClick={handleResetFilter} disabled={isFilterDisabled}>
              {t('general.reset')}
            </Button>
          </Box>
          <CreateAssessment
            patient_id={patient_id}
            disabled={disabled}
            setItemEditId={setItemEditId}
          />
        </Box>
        {rows.length > 0 && !loading ? (
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: `calc(100vh - ${tabPanelInstrumentHeight + 102}px)`,
            }}
          >
            <Table stickyHeader size='small'>
              <TableHead>
                <TableRow
                  sx={{
                    '& .MuiTableCell-head': {
                      paddingY: '1rem',
                      bgcolor: theme.palette.headerBackground.main,
                    },
                  }}
                >
                  {columns.map((col, index) => (
                    <TableCell key={`${col.id}-${index}`} align='left'>
                      {col.sortable ? (
                        <TableSortLabel
                          active={true}
                          direction={col.id === sort_by ? sort_order : 'asc'}
                          onClick={() =>
                            handleChangeOrder(col.id as ColumnNames)
                          }
                        >
                          {col.label}
                        </TableSortLabel>
                      ) : (
                        col.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(row => {
                  const isSelected =
                    isDrawerOpen && drawerDetails?.id === row.id;
                  const isDownloadDisabled = !row.completed || isDownloading;
                  const editIconColor = disabled ? 'disabled' : 'primary';
                  const downloadIconColor = isDownloadDisabled
                    ? 'disabled'
                    : 'primary';
                  const downloadTooltip = row.completed
                    ? 'Download PDF'
                    : 'Assessment incomplete';

                  return (
                    <TableRow
                      key={row.id}
                      onClick={() => handleRowClick(row)}
                      selected={isSelected}
                      sx={{
                        cursor: row.completed ? 'pointer' : 'unset',
                        '& .MuiTableCell-body': {
                          paddingY: '1rem',
                        },
                        ...(row.completed && {
                          '&:hover, &:nth-of-type(even):hover': {
                            backgroundColor: theme.palette.hoverTableRowBg.main,
                          },
                        }),
                        '&.Mui-selected, &.Mui-selected:hover, &:nth-of-type(even).Mui-selected, &:nth-of-type(even).Mui-selected:hover':
                          {
                            backgroundColor:
                              theme.palette.selectedTableRowBg.main,
                          },
                      }}
                    >
                      <TableCell align='left'>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                          }}
                        >
                          <Typography
                            variant='body2'
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <img
                              width='42px'
                              height='42px'
                              src={row.completed ? complete : incomplete}
                              style={{ objectFit: 'cover' }}
                              alt='completion'
                            />
                            {row.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align='left'>
                        <Typography variant='body2'>
                          {row.date
                            ? dayjs(row.date).format('MMM D, YYYY')
                            : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sortDirection='desc'
                        component='th'
                        scope='row'
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            gap: '0.5rem',
                            justifyContent: 'flex-end',
                          }}
                        >
                          <IconButton
                            disabled={disabled}
                            onClick={e => {
                              e.stopPropagation();
                              setItemEditId(row.id);
                            }}
                          >
                            <Edit
                              sx={{ fontSize: '24px' }}
                              color={editIconColor}
                            />
                          </IconButton>
                          <Tooltip title={downloadTooltip}>
                            <span>
                              <IconButton
                                disabled={isDownloadDisabled}
                                onClick={e => {
                                  e.stopPropagation();
                                  downloadPdf(patient_id, row.id);
                                }}
                              >
                                <Download
                                  sx={{ fontSize: '24px' }}
                                  color={downloadIconColor}
                                />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              sx={{
                borderBottom: 0,
                paddingY: '0.75rem',
                zIndex: 100,
                bgcolor: 'white',
                borderTop: theme => `1px solid ${theme.palette.grey[300]}`,
              }}
              component='div'
              count={totalCount}
              page={filters.page - 1}
              rowsPerPage={filters.limit}
              onPageChange={onPageIndexChange}
              onRowsPerPageChange={onPageSizeChange}
            />
          </TableContainer>
        ) : (
          <Box
            sx={{
              width: '100%',
              bgcolor: 'white',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: `calc(100vh - ${tabPanelInstrumentHeight}px)`,
            }}
          >
            {loading ? (
              <CircularProgress size='64px' />
            ) : (
              <TableNoResultPlaceholder />
            )}
          </Box>
        )}
      </Box>
      {drawerDetails && (
        <AssessmentDrawer
          onClose={handleCloseDrawer}
          open={isDrawerOpen}
          details={drawerDetails}
          updateAction={handleEditAssessment}
          downloadPdfAction={downloadPdf}
          patient_id={patient_id}
          isDownloading={isDownloading}
          disabled={disabled}
        />
      )}
    </>
  );
};
